import {WizardStepConfig} from 'platform/components';

import {ReactElement, useMemo, createElement} from 'react';
import {Control, useWatch} from 'react-hook-form';

import {
  SaleVehicle,
  useBaseVehicleWizard,
  useVehicleFormContext,
  StepBoundary,
  useWizardIndicators,
  SaleVehicleValidationSchema,
  useDebouncedWatch,
} from '@dms/features/vehicle-forms-kit';
import i18n from '@dms/i18n';
import {AuditCategoryUniqueKey} from '@dms/teas';

import {DOT_CHARACTER} from 'shared/web';

const overallEvaluationUniqueKeys = {
  [AuditCategoryUniqueKey.OVERALL_EVALUATION_OF_THE_EXTERIOR]:
    'entity.vehicle.labels.exteriorState',
  [AuditCategoryUniqueKey.OVERALL_EVALUATION_OF_THE_INTERIOR]:
    'entity.vehicle.labels.interiorState',
  [AuditCategoryUniqueKey.OVERALL_EVALUATION_OF_THE_TECHNICAL_CONDITION]:
    'entity.vehicle.labels.vehicleTechnicalCondition',
} as const;

const stepIds = ['photos', 'condition'] as const;

export const useSaleVehicleWizard = (
  control: Control<SaleVehicle>,
  stepContents: {
    basicInformation: ReactElement;
    vehicleData: ReactElement;
    features: ReactElement;
    condition: ReactElement;
    photos: ReactElement;
  }
): WizardStepConfig[] => {
  const wizardSteps = useBaseVehicleWizard({
    basicInformation: stepContents.basicInformation,
    vehicleData: stepContents.vehicleData,
    features: stepContents.features,
  });

  // Watch photos immediately (array length changes infrequently)
  const photos = useWatch({control, name: 'photos'});

  // Debounce auditData to reduce performance impact when audit fields change
  const auditData = useDebouncedWatch(control, ['auditData'], 300)[0];

  const {auditContext} = useVehicleFormContext();

  const structure = auditContext.getStructure();

  const auditStructure = useMemo(
    () =>
      structure.reduce(
        (acc, category) => {
          if (category.uniqueKey in overallEvaluationUniqueKeys) {
            const action = category.paramDefinitions?.relatedActions?.find(
              (param) => param.type === 'rating'
            );
            acc[`${category.id}_${action?.id}`] =
              overallEvaluationUniqueKeys[
                category.uniqueKey as keyof typeof overallEvaluationUniqueKeys
              ];
          }
          return acc;
        },
        {} as Record<string, string>
      ),
    [structure]
  );

  const auditState = useMemo(
    () =>
      Object.entries(
        Object.keys(auditStructure).length > 0 ? auditStructure : overallEvaluationUniqueKeys
      )
        // eslint-disable-next-line security/detect-object-injection
        .map(([key, labelKey]) => `${i18n.t(labelKey)}: ${auditData?.[key] || 'X'}`)
        .join(` ${DOT_CHARACTER} `),
    [auditData, auditStructure]
  );

  const indicators = useWizardIndicators({stepIds, schema: SaleVehicleValidationSchema});

  return [
    ...wizardSteps,
    {
      title: i18n.t('entity.vehicle.labels.photos'),
      content: createElement(StepBoundary, {id: 'photos'}, stepContents.photos),
      subtitle: photos?.length
        ? `${photos.length} ${i18n.t('entity.vehicle.labels.photos')}`
        : i18n.t('general.labels.notSpecified'),
      isBold: true,
      severity: indicators.photos?.severity,
    },
    {
      title: i18n.t('entity.vehicle.labels.vehicleCondition'),
      content: createElement(StepBoundary, {id: 'condition'}, stepContents.condition),
      subtitle: auditState,
      isBold: true,
      severity: indicators.condition?.severity,
    },
  ];
};
